import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import { toast } from 'react-toastify';
import api from '../services/api';

const AlumniDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalAlumni, setTotalAlumni] = useState(0);
  const [messages, setMessages] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('alumni_token');
    if (!token) {
      navigate('/alumni/login');
      return;
    }
    fetchProfile();
    fetchTotalAlumni();
  }, [navigate]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('alumni_token');
      const response = await api.get('/alumni/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setProfile(response.data.profile);
      } else {
        toast.error('Failed to load profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Session expired. Please login again.');
      navigate('/alumni/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalAlumni = async () => {
    try {
      const response = await api.get('/alumni/total-count');
      if (response.data.success) {
        setTotalAlumni(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching total alumni:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCourseDisplay = () => {
    if (!profile) return 'N/A';
    let display = profile.course_name || 'N/A';
    
    // TVET-CDACC - show Level number
    if (profile.exam_body_code === 'TVET-CDACC') {
      if (profile.level_number === 6) {
        display += ' (Level 6)';
      } else if (profile.level_number === 5) {
        display += ' (Level 5)';
      } else if (profile.level_number === 4) {
        display += ' (Level 4)';
      } else if (profile.level_number === 3) {
        display += ' (Level 3)';
      } else if (profile.level_number) {
        display += ` (Level ${profile.level_number})`;
      }
    }
    // KNEC - show Diploma, Certificate
    else if (profile.exam_body_code === 'KNEC') {
      if (profile.level_name === 'Diploma') {
        display += ' (Diploma)';
      } else if (profile.level_name === 'Certificate') {
        display += ' (Certificate)';
      } else if (profile.level_name === 'Artisan') {
        display += ' (Artisan)';
      } else if (profile.level_name) {
        display += ` (${profile.level_name})`;
      }
    }
    // NITA - show Grade
    else if (profile.exam_body_code === 'NITA') {
      if (profile.level_name === 'Grade I') {
        display += ' (Grade I)';
      } else if (profile.level_name === 'Grade II') {
        display += ' (Grade II)';
      } else if (profile.level_name === 'Grade III') {
        display += ' (Grade III)';
      } else if (profile.level_name) {
        display += ` (${profile.level_name})`;
      }
    }
    else if (profile.level_name) {
      display += ` (${profile.level_name})`;
    } else if (profile.level_number) {
      display += ` (Level ${profile.level_number})`;
    }
    
    return display;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Section */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome to your alumni portal</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Membership Status</p>
              <p className="text-2xl font-bold text-green-600">Active</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <span className="text-xl">✓</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Alumni Members</p>
              <p className="text-2xl font-bold text-blue-600">{totalAlumni}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <span className="text-xl">👥</span>
            </div>
          </div>
        </div>
      </div>

      {/* Membership Card */}
      <Card className="mb-6 bg-gradient-to-r from-primary to-primary-dark text-white">
        <div className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-white/80 text-sm">Alumni Membership Number</p>
              <p className="text-3xl font-bold mt-1">{profile?.alumni_membership_number || 'Pending...'}</p>
            </div>
            <div className="text-right">
              <p className="text-white/80 text-sm">Member Since</p>
              <p className="font-semibold mt-1">{formatDate(profile?.created_at)}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Profile Information */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b">Personal Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Full Name</label>
            <p className="text-gray-900 font-medium mt-1">{profile?.full_name}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Admission Number</label>
            <p className="text-gray-900 font-mono mt-1">{profile?.admin_number}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Email Address</label>
            <p className="text-gray-900 mt-1">{profile?.email}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Phone Number</label>
            <p className="text-gray-900 mt-1">{profile?.telephone}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Department</label>
            <p className="text-gray-900 mt-1">{profile?.department_name || 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Course</label>
            <p className="text-gray-900 mt-1">{getCourseDisplay()}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Exam Body</label>
            <p className="text-gray-900 mt-1">{profile?.exam_body_name || 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Grade</label>
            <p className="text-gray-900 mt-1">
              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                {profile?.grade || 'N/A'}
              </span>
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Graduation Year</label>
            <p className="text-gray-900 mt-1">{profile?.graduation_year || 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Certificate Number</label>
            <p className="text-gray-900 mt-1">{profile?.certificate_number || 'Pending'}</p>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>📌 Account Status:</strong>{' '}
              {profile?.is_active ? (
                <span className="text-green-600">Active ✓</span>
              ) : (
                <span className="text-red-600">Inactive - Please contact administrator</span>
              )}
            </p>
            <p className="text-xs text-blue-600 mt-2">
              Last login: {profile?.last_login ? formatDate(profile.last_login) : 'Never'}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AlumniDashboard;
